import { addProvider } from "../ServiceUtil/providerDescriptors";
import { IProviderDescriptor } from "../ServiceUtil/provider.descriptor.interface";
import { of as observableOf, Observable, BehaviorSubject, of, throwError } from "rxjs";
import { mergeMap, catchError, share } from "rxjs/operators";
import { AuthenticationService } from "../AuthenticationService";
import { UserManagementDelegate } from "./userManagement.delegate";
import { IBlockUnblockResponse, IUser, IUserResponse, IUserRoles, IUserRolecreate, IAddOrg, IUserRegistrationResponse, IAddClient, INewCraeteRole, IUserRole, IHMUser, IHMUserResponse, IDepartments, INewCreateDepartment, IDepartmentResponse, IMigrateLoginRequest, IOffboardUserRequest, IUserActionResponse,IUpdateUser}from "./userManagement.interface";
import { IUserRegistration } from "./userManagement.interface";
import { ConfigService } from "../ConfigService";

/**
 * UserManagementService - Manages user-related operations
 * 
 * ? Depends on AuthenticationService (optional)
 * ? Can check authentication before API calls
 * ? Shares HttpProvider with other services
 */
export class UserManagementService {
    private userManagementData: IUser[] = null;
    private userManagementSubject: BehaviorSubject<IUser[]> = new BehaviorSubject(null);
    public userManagement: Observable<IUser[]> = this.userManagementSubject;

    private allUsersSubject: BehaviorSubject<IUser[]> = new BehaviorSubject(null);
    public allusers: Observable<IUser[]> = this.allUsersSubject;

    private userRolesData: IUserRole[] = null
    private userRolesDataSubject: BehaviorSubject<IUserRole[]> = new BehaviorSubject(null);
    public userRoles: Observable<IUserRole[]> = this.userRolesDataSubject;

    public selectedUserId: string = "0";
    public selectedRoleId: string = "0";
    public selectedDepartmentId: number = 0;

    private selectedUserData: IUser = null;
    private selectedUserSubject: BehaviorSubject<IUser> = new BehaviorSubject(this.selectedUserData);
    public selectedUser: Observable<IUser> = this.selectedUserSubject;

    private usersFilteredSubject: BehaviorSubject<IUser[]> = new BehaviorSubject(null);
    public usersFiltered: Observable<IUser[]> = this.usersFilteredSubject;

    private hmUserManagementData: IHMUser[] = null;
    private hmUserManagementSubject: BehaviorSubject<IHMUser[]> = new BehaviorSubject(null);
    public hmUserManagement: Observable<IHMUser[]> = this.hmUserManagementSubject;

    private hmUserData: IHMUser[] = null;
    private hmUserSubject: BehaviorSubject<IHMUser[]> = new BehaviorSubject(null);
    public hmUser: Observable<IHMUser[]> = this.hmUserSubject;

    /**
     * Constructor
     * @param userManagementDelegate - Delegate for API calls
     * @param authenticationService - Optional AuthenticationService dependency ?
     */
    constructor(private userManagementDelegate: UserManagementDelegate,
        private authenticationService?: AuthenticationService,
        private configService?: ConfigService,
    )  // ? Make optional
    {
        console.log("?? UserManagementService instance created");
        if (this.authenticationService) {
            console.log("? AuthenticationService dependency injected");
        }
    }

    public get userId(): string {
        return this.selectedUserId;
    }

    public setSelectedUserId(id: string): void {
        this.selectedUserId = id;
    }

    public get roleId(): string {
        return this.selectedRoleId;
    }

    public setSelectedRoleId(roleId: string): void {
        this.selectedRoleId = roleId;
    }

    public get departmentId(): number {
        return this.selectedDepartmentId;
    }

    public setSelectedDepartmentId(departmentId: number): void {
        this.selectedDepartmentId = departmentId;
    }

    public getAllUsers(hardReload: boolean = false): Observable<boolean> {
        if (this.userManagementData !== null && this.userManagementData.length > 0 && hardReload === false) {
            return of(true);
        }
        const response = this.userManagementDelegate.getAllUsers();
        return this.processusers(response);
    }

    public getAllUserRoles(hardReload: boolean = false): Observable<boolean> {
        if (this.userRolesData !== null && this.userRolesData.length > 0 && hardReload === false) {
            return observableOf(true);
        }
        var response = this.userManagementDelegate.getAllUserRoles();
        return this.processRoles(response);
    }

    public getUserByUserId(userId: string): IUser {
        return this.userManagementData.find((advanceDataItem: IUser) => advanceDataItem.id === userId);
    }

    public createNewUserAccount(newuser: IUserRegistration): Observable<boolean> {
        newuser.clientTimeStamp = new Date().toISOString();
        const response = this.userManagementDelegate.createNewAccount({
            ...newuser

        });
        return this.processNewUser(response);
    }
    
    public updateUser(user: IUpdateUser): Observable<boolean> {
         user.clientTimeStamp = new Date().toISOString();
         const response = this.userManagementDelegate.updateUser(user);
         return this.processUser(response);
    }

    public getUserDetailsById(id: string): Observable<boolean> {
        if (id === "0") {
            this.selectedUserData = null;
            this.selectedUserSubject.next(this.selectedUserData);
            return observableOf(false);
        }
        var response = this.userManagementDelegate.getUserByUserId(id);
        return this.processUser(response);
    }

    public blockUser(userId: string): Observable<boolean> {
        const clientTimeStamp = new Date().toISOString();
        var response = this.userManagementDelegate.blockUser(userId, clientTimeStamp);
        return this.processBlockAndUnblock(response);
    }

    public unblockUser(userId: string): Observable<boolean> {
        var clientTimeStamp = new Date().toISOString();
        var response = this.userManagementDelegate.unblockUser(userId, clientTimeStamp);
        return this.processBlockAndUnblock(response);
    }

    public removeUser(userId: string, username: string): Observable<boolean> {
        var clientTimeStamp = new Date().toISOString();
        var result = this.userManagementDelegate.removeUser(userId, username, clientTimeStamp);
        return this.processUserRemove(result);

    }

    public filterUsers(searchTerm: string, filterBy: string) {
        let data = this.userManagementData;
        if (filterBy == null) {
            data = this.userManagementData.filter(x =>
                x.firstName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.email.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        else {
            data = this.userManagementData.filter(x =>
                x[filterBy].trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }
        this.usersFilteredSubject.next(data);
    }

    public addUserToRole(userrole: IUserRolecreate): Observable<boolean> {
        userrole.clientTimeStamp = new Date().toISOString();
        var result = this.userManagementDelegate.addUserToRole(userrole);
        return this.processUser(result);
    }

    public createRole(role: INewCraeteRole): Observable<boolean> {
        role.clientTimeStamp = this.authenticationService.getBaseRequest.clientTimeStamp;
        return this.userManagementDelegate.createRole(role);
    }

    public removeUserRole(roleId: string, userId: string): Observable<boolean> {
        var clientTimeStamp = this.authenticationService.getBaseRequest.clientTimeStamp;
        var result = this.userManagementDelegate.removeUserRole(roleId, userId, clientTimeStamp);
        return this.processUser(result);
    }

    public addUserToOrg(userrole: IAddOrg): Observable<boolean> {
        var result = this.userManagementDelegate.addUserToOrg(userrole);
        return this.processUser(result);
    }

    public removeUserFromOrg(userId: string, orgId: string): Observable<boolean> {
        var result = this.userManagementDelegate.removeUserFromOrg(userId, orgId);
        return this.processUser(result);
    }

    public addUserToClient(client: IAddClient): Observable<boolean> {
        const response = this.userManagementDelegate.addUserToClient(client);
        return this.processUser(response);
    }

    public removeUserFromClient(userId: string, clientId: string): Observable<boolean> {
        const response = this.userManagementDelegate.removeUserFromClient(userId, clientId);
        return this.processUser(response);
    }

    public getAllUsersByRoleName(roleName: string): Observable<boolean> {
        if (this.hmUserManagementData !== null && this.hmUserManagementData.length > 0) {
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();
        var response = this.userManagementDelegate.getAllHmUsers(orgId, roleName);
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(response: IHMUserResponse) {
            this.hmUserManagementData = response.hmFlowUsers;
            this.hmUserManagementSubject.next(this.hmUserManagementData);
        }

        function onFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public getHmUsersByRoleName(roleName: string): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        var response = this.userManagementDelegate.getAllHmUsers(orgId, roleName);
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(response: IHMUserResponse) {
            this.hmUserData = response.hmFlowUsers;
            this.hmUserSubject.next(this.hmUserData);
        }

        function onFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public getDepartmentById(departmentId: number): IDepartments {
        const departments = this.configService.configData.departments;
        if (!departments) { return null; }
        return departments.find(dept => dept.departmentId === departmentId);
    }
    public createDepartment(department: INewCreateDepartment): Observable<boolean> {
        department.clientTimeStamp = new Date().toISOString();
        const response = this.userManagementDelegate.createDepartment(department);
        return this.processDepartment(response);
    }
    public updateDepartment(department: IDepartments): Observable<boolean> {
        department.clientTimeStamp = new Date().toISOString();
        const response = this.userManagementDelegate.updateDepartment(department);
        return this.processDepartment(response);
    }

    private processusers(users: Observable<IUserResponse>): Observable<boolean> {
        users.subscribe(onSuccess.bind(this), onFault.bind(this));
        return users.pipe(mergeMap((response: IUserResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
        function onSuccess(userResponse: IUserResponse) {
            if (userResponse.success && userResponse.users.length > 0) {
                this.selectedUserId = userResponse.id ? userResponse.id : this.selectedUserId;
                this.userManagementData = userResponse.users;
                this.userManagementSubject.next(this.userManagementData);
                this.allUsersSubject.next(this.userManagementData);
                this.selectedUserData = this.userManagementData.find(x => x.id === this.selectedUserId);
                this.selectedUserSubject.next(this.selectedUserData);
                this.usersFilteredSubject.next(this.userManagementData);
            }
        }
        function onFault(error: any) {
            // UserManagementService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processUser(response: Observable<IUserResponse>): Observable<boolean> {
        response.subscribe(onUserSuccess.bind(this), onUserFault.bind(this));
        return response.pipe(mergeMap((response: IUserResponse) => observableOf(response.success)),
            catchError((error) => {
                return throwError(() => error);
            }),
            
            share());

        function onUserSuccess(userResponse: IUserResponse) {
            if (userResponse.success && userResponse.user) {
                this.selectedUserId = userResponse.user.id
                    ? userResponse.user.id
                    : this.selectedUserId;

                this.selectedUserData = userResponse.user;
                this.selectedUserSubject.next(this.selectedUserData);
            }
        }

        function onUserFault(error: any) {
           //EmployeeService.logger.error(`processEmployees Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processRoles(roles: Observable<IUserRoles>): Observable<boolean> {
        roles.subscribe(onSuccess.bind(this), onFault.bind(this));
        return roles.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onSuccess(roleResponse: IUserRoles) {
            if (roleResponse.success && roleResponse.roles.length > 0) {
                this.userRolesData = roleResponse.roles;
                this.userRolesDataSubject.next(this.userRolesData);
            }
        }
        function onFault(error: any) {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processBlockAndUnblock(blockUnblockResponse: Observable<IBlockUnblockResponse>): Observable<boolean> {
        blockUnblockResponse.subscribe(onSuccess.bind(this), onFault.bind(this));
        return blockUnblockResponse.pipe(mergeMap((response: IBlockUnblockResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(userResponse: IBlockUnblockResponse) {
            if (userResponse.success) {
                const user = this.userManagementData.find(
                    u => u.id === userResponse.userId
                );

                if (user) {
                    user.userStatus = userResponse.userStatus;
                }
                this.userManagementSubject.next(this.userManagementData);
            }
        }
        function onFault(error: any) {
            //SalesRepService.logger.error(`processSalesReps Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processNewUser(response: Observable<IUserRegistrationResponse>): Observable<boolean> {
        response.subscribe(onNewUserSuccess.bind(this), onNewUserFault.bind(this));
        return response.pipe(mergeMap((response: IUserRegistrationResponse) => observableOf(response.success)),
            catchError((error) => {
                return throwError(error);
            }),
            share());
        function onNewUserSuccess(newuserResponse: IUserRegistrationResponse) {
            if (newuserResponse.success) {
                this.selectedUserId = newuserResponse.id ? newuserResponse.id : this.selectedUserId;
            }
        }
        function onNewUserFault(error: any) {
            //EmployeeService.logger.error(`processEmployees Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processUserRemove(response: Observable<boolean>): Observable<boolean> {
        response.subscribe(onUserRemoveSuccess.bind(this), onUserRemoveFault.bind(this));
        return response.pipe(mergeMap((response: boolean) => observableOf(response)),
            catchError(() => observableOf(false)),
            share());
        function onUserRemoveSuccess(success: boolean) {
            if (success) {
                this.getAllUsers(true);
            }
        }
        function onUserRemoveFault(error: any) {
            // EmployeeService.logger.error(`processEmployees Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processDepartment(department: Observable<IDepartmentResponse>): Observable<boolean> {
        department.subscribe(onSuccess.bind(this), onFault.bind(this));
        return department.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onSuccess(deptResponse: IDepartmentResponse) {
            if (deptResponse.success) {
                if (!this.configService.configData.departments) {
                    this.configService.configData.departments = [];
                }
                const departments = this.configService.configData.departments;
                const index = departments.findIndex(x => x.departmentId === deptResponse.department.departmentId);

                if (index !== -1) {
                    departments[index] = deptResponse.department;
                } else {
                    departments.push(deptResponse.department);
                }
                this.selectedDepartmentId = deptResponse.departmentId ? deptResponse.departmentId : this.selectedDepartmentId;
            }
        }
        function onFault(error: any) {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public migrateLogin(employeeId: string): Observable<boolean> {
        const clientTimeStamp = this.authenticationService.getBaseRequest.clientTimeStamp;
        const req: IMigrateLoginRequest = { employeeId, clientTimeStamp };
        return this.userManagementDelegate.migrateLoginToSBaseEmail(req).pipe(
            mergeMap((response: IUserActionResponse) => of(response.success === true)),
            catchError(() => of(false))
        );
    }

    public offboardEmployee(employeeId: string): Observable<boolean> {
        const clientTimeStamp = this.authenticationService.getBaseRequest.clientTimeStamp;
        const initiatedBy = this.authenticationService.getBaseRequest.userName;
        const req: IOffboardUserRequest = { employeeId, initiatedBy, clientTimeStamp };
        return this.userManagementDelegate.offboardUser(req).pipe(
            mergeMap((response: IUserActionResponse) => of(response.success === true)),
            catchError(() => of(false))
        );
    }
}


